/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import { LinkButton, Paragraph } from '@cambridgeassessment/cambridge-ui';
import { Link } from 'react-router-dom';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ReportType, SortOrder, Roles } from '@cambridgeassessment/lib-ee-commons';
import { StyledContainer } from '../../../Content.styles';
import { TableBody, TableHead, TableRow, Table } from '@material-ui/core';
import {
  SearchResultsTableCell,
  StyledTableContainer,
  StyledImportExportIcon,
  StyledHeadline,
  StyledReportLink,
  useStyles,
  StyledTableWrapper,
  StyledDownloadLinkWrapper,
} from './MalpracticeReportsSummary.styles';
import { RootState } from '../../../../../store';
import * as thunks from '../../../../../store/malpracticeReports/thunks';
import Loader from '../../../../../components/Loader/Loader';
import * as actions from '../../../../../store/malpracticeReports/actions';
import { ReportSummary, SearchQuery } from '../../../../../types/Plagiarism';
import ReportTable, { SortingParams, useSorting } from '../ReportTable/ReportTable';
import { StyledTitle } from '../ReportTable/ReportTable.styles';
import { Authorized } from 'lib-browser';

interface Props {
  match: {
    params: {
      seriesId: number;
    };
  };
  generateUrl: (url: string, query?: object) => string;
  searchQuery?: SearchQuery;
}

export const BlackImportExportIcon = (): React.ReactElement => {
  return <StyledImportExportIcon />;
};

interface ReportSummaryLinkProps {
  count: number;
  reportSummary: ReportSummary;
  low: number;
  high: number;
  seriesId: number;
  generateUrl: (url: string, query?: object) => string;
}

const ReportSummaryLink: React.FC<ReportSummaryLinkProps> = ({
  count,
  low,
  high,
  reportSummary,
  seriesId,
  generateUrl,
}) => {
  const searchQueryParams = { low, high };
  const path = `/malpracticeReports/${seriesId}/${reportSummary.assessmentCode}/${reportSummary.componentNumber}/${reportSummary.sectionId}/details`;

  if (count > 0) {
    return <Link to={generateUrl(path, searchQueryParams)}>{count}</Link>;
  }

  return <>{count}</>;
};

const MalpracticeReportsSummary: React.FC<Props> = ({
  match: {
    params: { seriesId },
  },
  generateUrl,
  searchQuery = {},
}) => {
  const dispatch = useDispatch();
  const {
    malpracticeReport: { summary, seriesData },
    userData: { selectedRole },
  } = useSelector((state: RootState) => state);

  const chosenSeries = seriesData?.find((series) => series.id === +seriesId);
  const reports = summary.reportSummary;

  const loadMoreItems = (sortingParams?: SortingParams<ReportSummary>): void => {
    dispatch(thunks.getReportData(searchQuery, sortingParams));
  };

  useEffect(() => {
    loadMoreItems();
    return (): void => {
      dispatch(actions.resetState());
    };
  }, []);

  const { sort: onSort } = useSorting<ReportSummary>(
    (sortingParams) => {
      dispatch(actions.resetState());
      loadMoreItems(sortingParams);
    },
    {
      fieldName: 'assessmentCode',
      order: SortOrder.ASC,
    },
  );
  const onDownloadClick = (): void => {
    dispatch(thunks.getReport(+seriesId, chosenSeries?.name || '', ReportType.PLAGIARISM_ALL_CANDIDATES));
  };

  const downloadByQueryReport = () => {
    dispatch(thunks.getReport(+seriesId, chosenSeries?.name || '', ReportType.PLAGIARISM_BY_QUERY, searchQuery));
  };

  const downloadAllErrorsReport = (): void => {
    dispatch(thunks.getReport(+seriesId, chosenSeries?.name || '', ReportType.PLAGIARISM_ERRORS));
  };

  const classes = useStyles();

  const renderItem = (low: number, high: number, count: number, row: ReportSummary) => {
    return (
      <ReportSummaryLink
        low={low}
        high={high}
        count={count}
        generateUrl={generateUrl}
        reportSummary={row}
        seriesId={seriesId}
      />
    );
  };

  if (!reports) return <Loader />;

  return (
    <Authorized
      selectedRole={selectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
    >
      <StyledContainer data-qa-id='page-priming-malpractice-reports'>
        <StyledHeadline>{chosenSeries?.name}</StyledHeadline>
        <Paragraph>View all the available results for this series</Paragraph>
      </StyledContainer>
      <StyledReportLink>
        <LinkButton
          onClick={onDownloadClick}
          dataQaId='download-all-candidates-report'
          passedClassName={classes.root}
          arrowType='up'
          endIcon={false}
        >
          Download All Candidates Report.csv
        </LinkButton>
      </StyledReportLink>
      <StyledReportLink>
        <LinkButton
          onClick={downloadAllErrorsReport}
          dataQaId='download-turnitin-error-report'
          passedClassName={classes.root}
          arrowType='up'
          endIcon={false}
        >
          Download All Turnitin Errors.csv
        </LinkButton>
      </StyledReportLink>
      <StyledTableContainer>
        <StyledTitle>Results</StyledTitle>
        <Table size='small' data-qa-id='search-query-table'>
          <TableHead>
            <TableRow>
              <SearchResultsTableCell>Syllabus</SearchResultsTableCell>
              <SearchResultsTableCell>Component</SearchResultsTableCell>
              <SearchResultsTableCell>Centre number</SearchResultsTableCell>
              <SearchResultsTableCell>Candidate number</SearchResultsTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <SearchResultsTableCell data-qa-id='report-results-syllabus'>
                {searchQuery.assessmentCode || 'n/a'}
              </SearchResultsTableCell>
              <SearchResultsTableCell data-qa-id='report-results-component'>
                {searchQuery.componentNumber || 'n/a'}
              </SearchResultsTableCell>
              <SearchResultsTableCell data-qa-id='report-results-centre-number'>
                {searchQuery.centreId || 'n/a'}
              </SearchResultsTableCell>
              <SearchResultsTableCell data-qa-id='report-results-candidate-number'>
                {searchQuery.learnerNumber || 'n/a'}
              </SearchResultsTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </StyledTableContainer>
      <StyledTableWrapper>
        <StyledDownloadLinkWrapper>
          <LinkButton
            onClick={downloadByQueryReport}
            dataQaId='download-summary-report-link'
            passedClassName={classes.root}
            arrowType='up'
            endIcon={false}
          >
            Download Full Summary Report.csv
          </LinkButton>
        </StyledDownloadLinkWrapper>
        <ReportTable
          data={reports}
          columns={[
            {
              label: 'Syllabus',
              name: 'assessmentCode',
              centre: false,
              sortable: true,
            },
            {
              label: 'Component',
              name: 'componentNumber',
              centre: false,
              sortable: true,
            },
            {
              label: 'Component sections',
              name: 'sectionName',
              centre: false,
              sortable: true,
            },
            {
              label: '0-49%',
              name: 'lowCount',
              centre: true,
              sortable: true,
              renderItem: (row) => renderItem(0, 49, row.lowCount, row),
            },
            {
              label: '50-65%',
              name: 'midCount',
              centre: true,
              sortable: true,
              renderItem: (row) => renderItem(50, 65, row.midCount, row),
            },
            {
              label: '66-100%',
              name: 'highCount',
              centre: true,
              sortable: true,
              renderItem: (row) => renderItem(66, 100, row.highCount, row),
            },
            {
              label: 'Turnitin errors',
              name: 'errorCount',
              centre: true,
              sortable: true,
              renderItem: function TurnItInErrorsLink(row: ReportSummary) {
                if (row.errorCount > 0) {
                  return (
                    <Link
                      to={generateUrl(
                        `/malpracticeReports/${seriesId}/${row.assessmentCode}/${row.componentNumber}/${row.sectionId}/errors`,
                      )}
                    >
                      {row.errorCount}
                    </Link>
                  );
                }

                return <>{row.errorCount}</>;
              },
            },
          ]}
          loadMoreItems={loadMoreItems}
          onSort={onSort}
        />
      </StyledTableWrapper>
    </Authorized>
  );
};

export default MalpracticeReportsSummary;
